import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Calendar, Mail, Phone, User, MessageSquare, PartyPopper } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const eventTypes = [
    'Music Festival',
    'Concert',
    'Multi-Day Event',
    'Large Corporate Event',
    'Sporting Event',
    'Other',
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        event_type: '',
        event_date: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#050505] noise-bg"
    >
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FF0099] text-sm font-medium tracking-widest uppercase mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">BOOK</span>{' '}
            <span className="text-gradient">SLUSH'D</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Ready to bring the chill to your event? Fill out the form below and 
            we'll get back to you within 24 hours.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="glass-card rounded-2xl p-8 md:p-12"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Name */}
            <div className="relative">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Your Name *
              </label>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent"
                  placeholder="John Doe"
                />
              </div>
            </div>

            {/* Email */}
            <div className="relative">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="relative">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            {/* Event Type */}
            <div className="relative">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Event Type *
              </label>
              <div className="relative">
                <PartyPopper className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleChange}
                  required
                  data-testid="contact-event-type-select"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent cursor-pointer appearance-none"
                >
                  <option value="" disabled className="bg-[#1A1A1A]">Select event type</option>
                  {eventTypes.map((type) => (
                    <option key={type} value={type} className="bg-[#1A1A1A]">
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Event Date */}
            <div className="relative md:col-span-2">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Event Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="date"
                  name="event_date"
                  value={formData.event_date}
                  onChange={handleChange}
                  data-testid="contact-date-input"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent"
                />
              </div>
            </div>

            {/* Message */}
            <div className="relative md:col-span-2">
              <label className="text-white/50 text-sm uppercase tracking-wider mb-2 block">
                Tell Us About Your Event *
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-0 top-4 w-5 h-5 text-white/30" />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-message-textarea"
                  className="w-full pl-8 py-3 neon-input text-white bg-transparent resize-none"
                  placeholder="Expected attendance, location, any special requirements..."
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="contact-submit-btn"
            className="w-full mt-10 bg-[#CCFF00] text-black font-bold text-lg py-5 rounded-full neon-glow-hover transition-all duration-300 uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span>Sending...</span>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send Message</span>
              </>
            )}
          </motion.button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-success-message"
              className="mt-6 text-center text-[#CCFF00] font-medium"
            >
              🎉 Message sent successfully! We'll be in touch soon.
            </motion.div>
          )}
          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-error-message"
              className="mt-6 text-center text-[#FF0099] font-medium"
            >
              Something went wrong. Please try again or email us directly.
            </motion.div>
          )}
        </motion.form>
      </div>

      {/* Decorative Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-2xl h-64 bg-[#CCFF00]/5 blur-[100px] rounded-full" />
    </section>
  );
};

export default ContactForm;
